import { useMemo, useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ProgramsListCompactUI } from './ProgramsListCompactUI';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as programsSelectors from '@makemydeal/dr-dash-store/selectors/programsSelectors';
import * as programsPreferencesSelectors from '@makemydeal/dr-dash-store/selectors/programsPreferencesSelectors';
import * as programActionCreators from '@makemydeal/dr-dash-store/actions/programActionCreators';
import * as programsPreferencesActionCreators from '@makemydeal/dr-dash-store/actions/programsPreferencesActionCreators';
import { mapPaymentResponseToProgramQuote } from '@makemydeal/dr-dash-store/utils/programUtils';
import { Alert } from '@interstate/components/Alert';
import { isProgramNotExpired } from './utils';
import { resolveProgramLabel } from './noProgramMessage';
import { Typography } from '@interstate/components/Typography';
import { Spinner } from '@interstate/components/Spinner';
import { Box } from '@interstate/components/Box';
import { Button } from '@interstate/components/Button';
import { FunnelIcon, LockClosedIcon } from '@interstate/components/Icons';
import { FilterChip, LinkChip } from '@interstate/components/Chip';
import { Action } from '@interstate/components/Action';
import { LoadingIndicatorWrapper, ListWithOverlayWrapper } from './styles';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { CASH } from '@makemydeal/dr-platform-types';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import { ProgramsFiltersSlideOut } from './ProgramsFiltersSlideOut';
import type { ProgramFilters } from './ProgramsFilters.types';
import { DEFAULT_FILTERS, NO_RESULTS_MESSAGE, MAX_VISIBLE_FILTER_CHIPS } from './ProgramsFilters.types';
import {
    getCustomerCreditTier,
    getCustomerCreditScore,
    getScoreForTier,
    isValidScore,
    CREDIT_TIER_OPTIONS,
    ALL_CREDIT_TIERS_VALUE
} from './creditTiers';
import type { CreditTierKey } from './creditTiers';
import type { GetAllQuotesOptions } from '@makemydeal/dr-dash-store/types/types';
import type { ProgramQuote, ProgramsSortSelection, StateTree } from '@makemydeal/dr-dash-types';
import {
    filterPrograms,
    hasActiveFilters,
    getAppliedFilterTags,
    removeFilterValue,
    extractAllFilterOptions
} from './ProgramsFilters.utils';

const ERROR_DESC = 'No Programs Found';
const LOADING_DESC = 'Loading Programs...';
const NO_PROGRAMS: ProgramQuote[] = [];

const getSelectedTier = (filters: ProgramFilters): string | undefined => filters.creditTiers[0];

const buildQuoteOptions = (
    tier: string | undefined,
    customerTier: CreditTierKey | undefined,
    customerScore: number | undefined
): GetAllQuotesOptions => {
    return { creditScoreOverride: getScoreForTier(tier, customerTier, customerScore) };
};

const buildInitialFilters = (
    allTiersMode: boolean,
    manualScore: number | undefined,
    tierScore: { low: number; high: number },
    dealTermMonths: number | undefined
): ProgramFilters => {
    const customerTier = getCustomerCreditTier(manualScore, tierScore);
    const seededFilters: Partial<Record<keyof ProgramFilters, string[]>> = allTiersMode
        ? { creditTiers: [ALL_CREDIT_TIERS_VALUE] }
        : {
              creditTiers: customerTier ? [customerTier] : [],
              terms: dealTermMonths != null ? [dealTermMonths.toString()] : []
          };
    return { ...DEFAULT_FILTERS, ...seededFilters };
};

export const Programs = () => {
    const dispatch = useDispatch();

    const quotes = useSelector(programsSelectors.getActivePrograms);
    const isError = useSelector(programsSelectors.getProgramsLoadingError);
    const isLoading = useSelector(programsSelectors.getProgramsLoadingStatus);
    const isCalculatingPayment = useSelector(offerReduxSelectors.isCalculatingPayment);
    const isFiltersProgramIncentivesEnabled = useSelector(featureToggleSelectors.isFiltersProgramIncentivesEnabled);
    const isPIScreenEnhancementsEnabled = useSelector(featureToggleSelectors.isPIScreenEnhancementsEnabled);
    const allTiersMode = useSelector(featureToggleSelectors.isProgramIncentivesRestorationEnabled);
    const isCashFixesEnabled = useSelector(featureToggleSelectors.isCashDealIncentivesFixesEnabled);
    const isExplicitLenderP1Enabled = useSelector(featureToggleSelectors.isExplicitLenderP1Enabled);
    const isStickyFiltersEnabled = useSelector(featureToggleSelectors.isStickyFiltersAndSortingEnabled);
    const isMilesYearConsistencyEnabled = useSelector(featureToggleSelectors.isProgramsMilesYearConsistencyEnabled);
    const shouldGatePrograms = useSelector(vehicleSelectors.getIsQuoteWithoutVehicle);
    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const lenderLegalName = useSelector(offerReduxSelectors.getLenderLegalName);
    const retainedProgram = useSelector(programsSelectors.getRetainedProgramDisplay);
    const term = useSelector((state) => offerReduxSelectors.getSelectedTerm(state, offerType));
    const programName = term?.lenderProgram?.programName;
    const manualCreditScore = useSelector(offerReduxSelectors.getManualCreditScore);
    const customerTierScore = useSelector(offerReduxSelectors.getSelectedCreditTierScore);
    const persistedCreditScoreOverride = useSelector(programsSelectors.getCreditScoreOverride);
    const allTiersLoadedForOfferType = useSelector(programsSelectors.getAllTiersLoadedForOfferType);
    const allTiersInputsKey = useSelector(programsSelectors.getAllTiersInputsKey);
    const programQuotesInputsKey = useSelector(programsSelectors.getProgramQuotesInputsKey);
    const primaryTierLoaded = useSelector(programsSelectors.getPrimaryTierLoaded);
    const loadedTierCount = useSelector(programsSelectors.getLoadedTierCount);
    const allProgramsLoaded = useSelector(programsSelectors.getAllProgramsLoaded);
    const isTermCatalogUnnamed = useSelector(programsSelectors.isSelectedTermCatalogUnnamed);
    const selectedTermId = useSelector((state) => offerReduxSelectors.getTermIdByOfferType(state, offerType));
    const pendingApplyProgram = useSelector(programsSelectors.getPendingApplyProgram);
    const stickyOfferType = isStickyFiltersEnabled && offerType !== CASH ? offerType : undefined;
    const persistedFilters = useSelector((state: StateTree) =>
        programsPreferencesSelectors.getProgramsFilterPreferences(state, stickyOfferType)
    );
    const persistedSort = useSelector((state: StateTree) =>
        programsPreferencesSelectors.getProgramsSortPreference(state, stickyOfferType)
    );

    const initialScore = persistedCreditScoreOverride ?? manualCreditScore;
    const dealTermMonths = term?.months;
    const [filterSlideOutOpen, setFilterSlideOutOpen] = useState(false);
    const [filters, setFilters] = useState<ProgramFilters>(
        () => persistedFilters ?? buildInitialFilters(allTiersMode, initialScore, customerTierScore.score, dealTermMonths)
    );
    const [appliedFilters, setAppliedFilters] = useState<ProgramFilters>(filters);
    const [isFilterChipsExpanded, setIsFilterChipsExpanded] = useState(false);
    const [isRefetching, setIsRefetching] = useState(false);
    const [reorderTrigger, setReorderTrigger] = useState(0);
    const bumpReorderTrigger = useCallback(() => setReorderTrigger((value) => value + 1), []);

    const lastFetchedTier = useRef<string | undefined | null>(null);
    const unnamedCatalogReloadedFor = useRef<string | undefined>(undefined);
    const lastFetchSucceeded = useRef(true);

    const programsNotExpired = useMemo(
        () => quotes.map((quote) => mapPaymentResponseToProgramQuote(quote, isExplicitLenderP1Enabled)).filter(isProgramNotExpired),
        [quotes, isExplicitLenderP1Enabled]
    );

    const filterablePrograms = isFiltersProgramIncentivesEnabled ? programsNotExpired : NO_PROGRAMS;

    const allFilterOptions = useMemo(
        () => extractAllFilterOptions(filterablePrograms, isPIScreenEnhancementsEnabled),
        [filterablePrograms, isPIScreenEnhancementsEnabled]
    );

    const filteredPrograms = useMemo(
        () => filterPrograms(programsNotExpired, appliedFilters, offerType, isPIScreenEnhancementsEnabled),
        [programsNotExpired, appliedFilters, offerType, isPIScreenEnhancementsEnabled]
    );

    const programLabel = resolveProgramLabel({
        offerType,
        allTiersMode,
        quotes: programsNotExpired,
        months: term?.months,
        lenderLegalName,
        programName,
        retainedLenderLegalName: retainedProgram?.lenderLegalName,
        retainedProgramName: retainedProgram?.programName
    });

    const showFilterChips = useMemo(
        () => hasActiveFilters(appliedFilters, offerType, isPIScreenEnhancementsEnabled),
        [appliedFilters, offerType, isPIScreenEnhancementsEnabled]
    );

    const appliedFilterTags = useMemo(
        () => getAppliedFilterTags(appliedFilters, offerType, allFilterOptions, isPIScreenEnhancementsEnabled),
        [appliedFilters, offerType, allFilterOptions, isPIScreenEnhancementsEnabled]
    );

    const visibleFilterTags = isFilterChipsExpanded ? appliedFilterTags : appliedFilterTags.slice(0, MAX_VISIBLE_FILTER_CHIPS);
    const showMoreLessToggle = appliedFilterTags.length > MAX_VISIBLE_FILTER_CHIPS;
    const allTiersPending = allTiersMode && !primaryTierLoaded;
    const showLoading = isLoading || isRefetching || allTiersPending;
    const hasExistingPrograms = filteredPrograms.length > 0;
    const showInitialLoading = allTiersMode
        ? allTiersPending || ((isLoading || isRefetching) && !hasExistingPrograms)
        : showLoading;
    const isRecalculating =
        allTiersMode && !allTiersPending && (isLoading || isRefetching || isCalculatingPayment) && hasExistingPrograms;
    const hasNoResults =
        !showLoading &&
        !isError &&
        filteredPrograms.length === 0 &&
        programsNotExpired.length > 0 &&
        (!allTiersMode || allProgramsLoaded);

    const seedTier = useMemo(
        () => getCustomerCreditTier(initialScore, customerTierScore.score),
        [initialScore, customerTierScore.score]
    );

    const dealTier = useMemo(
        () => getCustomerCreditTier(manualCreditScore, customerTierScore.score),
        [manualCreditScore, customerTierScore.score]
    );
    const dealScore = useMemo(
        () => getCustomerCreditScore(manualCreditScore, customerTierScore.score),
        [manualCreditScore, customerTierScore.score]
    );

    const refetchIfCreditTierChanged = useCallback(
        (nextFilters: ProgramFilters) => {
            if (allTiersMode) return;
            const nextTier = getSelectedTier(nextFilters);
            const nextOptions = buildQuoteOptions(nextTier, dealTier, dealScore);
            if ('creditScoreOverride' in nextOptions && !isValidScore(nextOptions.creditScoreOverride)) return;
            if (lastFetchSucceeded.current && nextTier === lastFetchedTier.current) return;
            lastFetchedTier.current = nextTier;
            setIsRefetching(true);
            dispatch(programActionCreators.getProgramQuotes(nextOptions));
        },
        [dispatch, dealTier, dealScore, allTiersMode]
    );

    const commitFilters = useCallback(
        (next: ProgramFilters) => {
            setFilters(next);
            setAppliedFilters(next);
            if (stickyOfferType) dispatch(programsPreferencesActionCreators.setProgramsFilters(stickyOfferType, next));
            refetchIfCreditTierChanged(next);
            bumpReorderTrigger();
        },
        [refetchIfCreditTierChanged, bumpReorderTrigger, stickyOfferType, dispatch]
    );

    const handleApplyFilters = useCallback(() => commitFilters(filters), [commitFilters, filters]);
    const handleClearFilters = useCallback(
        () =>
            commitFilters(
                allTiersMode
                    ? { ...DEFAULT_FILTERS, creditTiers: [ALL_CREDIT_TIERS_VALUE] }
                    : { ...DEFAULT_FILTERS, creditTiers: seedTier ? [seedTier] : [] }
            ),
        [commitFilters, allTiersMode, seedTier]
    );
    const handleRemoveFilterTag = useCallback(
        (filterKey: keyof ProgramFilters, value: string) => {
            commitFilters(removeFilterValue(appliedFilters, filterKey, value));
        },
        [appliedFilters, commitFilters]
    );

    const loadAllTiers = useCallback(() => {
        const currentTier = dealTier ?? seedTier;
        const primaryOption = CREDIT_TIER_OPTIONS.find((option) => option.value === currentTier) ?? CREDIT_TIER_OPTIONS[0];
        // Credit tiers are a lending concept that doesn't apply to cash deals: fire only the
        // single primary (includeIncentives:true) request instead of fanning out across tiers.
        // The extra non-primary (includeIncentives:false) responses have no purpose for cash and
        // were racing the primary response, intermittently clobbering the incentives catalog
        // that had just been populated (DE759607/US2076398).
        const isCashFixActive = offerType === CASH && isCashFixesEnabled;
        const orderedOptions = isCashFixActive
            ? [primaryOption]
            : [primaryOption, ...CREDIT_TIER_OPTIONS.filter((option) => option.value !== primaryOption.value)];
        orderedOptions.forEach((option) => {
            dispatch(
                programActionCreators.getProgramQuotes({
                    creditScoreOverride: option.score,
                    requestedTier: option.value,
                    primary: option.value === primaryOption.value
                })
            );
        });
    }, [dispatch, dealTier, seedTier, offerType, isCashFixesEnabled]);

    const handleOpenFilters = useCallback(() => {
        setFilters(appliedFilters);
        setFilterSlideOutOpen(true);
        bumpReorderTrigger();
    }, [appliedFilters, bumpReorderTrigger]);

    const handleCloseFilters = useCallback(() => {
        setFilters(appliedFilters);
        setFilterSlideOutOpen(false);
    }, [appliedFilters]);

    const handleSortChange = useMemo(
        () =>
            stickyOfferType
                ? (sort: ProgramsSortSelection | null) =>
                      dispatch(programsPreferencesActionCreators.setProgramsSort(stickyOfferType, sort))
                : undefined,
        [stickyOfferType, dispatch]
    );

    useEffect(() => {
        if (isRefetching) {
            setIsRefetching(false);
            lastFetchSucceeded.current = !isError;
        }
    }, [quotes, isError]);

    useEffect(() => {
        if (!isTermCatalogUnnamed) unnamedCatalogReloadedFor.current = undefined;
    }, [isTermCatalogUnnamed]);

    const isPricingInFlight = isCalculatingPayment || Boolean(pendingApplyProgram);

    useEffect(() => {
        if (!allTiersMode) return;
        const reloadUnnamedCatalog =
            isTermCatalogUnnamed && !isPricingInFlight && unnamedCatalogReloadedFor.current !== selectedTermId;
        const reloadForInputs = isMilesYearConsistencyEnabled && allTiersInputsKey !== programQuotesInputsKey && !isPricingInFlight;
        if (allTiersLoadedForOfferType === offerType && !reloadUnnamedCatalog && !reloadForInputs) return;
        if (isTermCatalogUnnamed) unnamedCatalogReloadedFor.current = selectedTermId;
        loadAllTiers();
        const isInputsReload = allTiersLoadedForOfferType === offerType && reloadForInputs;
        const inputsKey = isMilesYearConsistencyEnabled ? programQuotesInputsKey : undefined;
        dispatch(programActionCreators.setAllTiersLoaded(offerType, inputsKey, isInputsReload));
    }, [
        dispatch,
        allTiersMode,
        isMilesYearConsistencyEnabled,
        offerType,
        allTiersLoadedForOfferType,
        allTiersInputsKey,
        programQuotesInputsKey,
        loadAllTiers,
        isTermCatalogUnnamed,
        selectedTermId,
        isPricingInFlight
    ]);

    useEffect(() => {
        if (!allTiersMode || allProgramsLoaded) return;
        // Cash fires a single request (no credit-tier fan-out - see loadAllTiers), so it can never
        // reach CREDIT_TIER_OPTIONS.length; compare against the number of requests actually fired
        // instead, so this only flips once that request's response has actually landed rather than
        // eagerly when it's merely fired.
        const expectedTierCount = offerType === CASH && isCashFixesEnabled ? 1 : CREDIT_TIER_OPTIONS.length;
        if (loadedTierCount >= expectedTierCount) {
            dispatch(programActionCreators.allProgramsLoaded());
        }
    }, [allTiersMode, allProgramsLoaded, loadedTierCount, offerType, isCashFixesEnabled, dispatch]);

    useEffect(() => {
        if (shouldGatePrograms) return;
        if (allTiersMode) return;
        const restoredTier = persistedFilters ? getSelectedTier(persistedFilters) : undefined;
        const mountTier = restoredTier ?? seedTier;
        const mountOverride = getScoreForTier(mountTier, dealTier, dealScore);
        const mountOptions: GetAllQuotesOptions | undefined = isValidScore(mountOverride)
            ? { creditScoreOverride: mountOverride }
            : undefined;
        lastFetchedTier.current = mountTier;
        dispatch(programActionCreators.getProgramQuotes(mountOptions));
        return () => {
            dispatch(programActionCreators.clear());
        };
    }, []);

    const isInitialDealScoreSync = useRef(true);
    useEffect(() => {
        if (isInitialDealScoreSync.current) {
            isInitialDealScoreSync.current = false;
            return;
        }
        if (allTiersMode) return;
        if (getSelectedTier(appliedFilters) === dealTier) {
            lastFetchedTier.current = null;
            refetchIfCreditTierChanged(appliedFilters);
        }
    }, [dealScore, dealTier]);

    const prevOfferTypeRef = useRef(offerType);
    useEffect(() => {
        if (prevOfferTypeRef.current === offerType) return;
        prevOfferTypeRef.current = offerType;
        const nextFilters =
            persistedFilters ??
            (stickyOfferType
                ? buildInitialFilters(allTiersMode, initialScore, customerTierScore.score, dealTermMonths)
                : undefined);
        if (nextFilters) {
            setFilters(nextFilters);
            setAppliedFilters(nextFilters);
            if (stickyOfferType && !persistedFilters) {
                dispatch(programsPreferencesActionCreators.setProgramsFilters(stickyOfferType, nextFilters));
            }
            refetchIfCreditTierChanged(nextFilters);
            return;
        }
        if (!isPIScreenEnhancementsEnabled) return;
        setAppliedFilters((prev) => ({ ...prev, ratePrograms: [], lenderTiers: [] }));
        setFilters((prev) => ({ ...prev, ratePrograms: [], lenderTiers: [] }));
    }, [offerType, isPIScreenEnhancementsEnabled]);

    useEffect(() => {
        if (
            isPIScreenEnhancementsEnabled &&
            programsNotExpired.length > 0 &&
            allFilterOptions.ratePrograms.length === 0 &&
            appliedFilters.ratePrograms.length > 0
        ) {
            setAppliedFilters((prev) => ({ ...prev, ratePrograms: [] }));
            setFilters((prev) => ({ ...prev, ratePrograms: [] }));
        }
    }, [isPIScreenEnhancementsEnabled, allFilterOptions.ratePrograms.length, programsNotExpired.length]);

    return (
        <Box sx={{ marginBottom: '1rem', flex: '1 0 auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0.3rem 0 0.5rem' }}>
                <Typography variant="h3" sx={{ margin: 0 }}>
                    Active Programs
                </Typography>
                {isFiltersProgramIncentivesEnabled && (
                    <Button
                        buttonStyle="secondary"
                        startIcon={<FunnelIcon />}
                        data-testid="programs-filters-button"
                        size="small"
                        onClick={handleOpenFilters}
                    >
                        Filters
                    </Button>
                )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '1rem 0 1rem' }}>
                <Typography variant="body-md" sx={{ margin: 0 }} data-testid="selected-program-name">
                    {programLabel}
                </Typography>
                {programsNotExpired.length > 0 && (
                    <Typography variant="body-sm" sx={{ margin: 0 }} data-testid="programs-count">
                        {filteredPrograms.length === programsNotExpired.length
                            ? `${programsNotExpired.length} Programs`
                            : `${filteredPrograms.length} from ${programsNotExpired.length} Programs`}
                    </Typography>
                )}
            </Box>
            {showFilterChips && (
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '0.5rem',
                        alignItems: 'center',
                        marginBottom: '0.75rem'
                    }}
                    data-testid="applied-filter-tags"
                >
                    {visibleFilterTags.map((tag) => {
                        const isLockedTier = !allTiersMode && tag.filterKey === 'creditTiers';
                        return (
                            <FilterChip
                                key={`${tag.filterKey}-${tag.value}`}
                                data-testid={`filter-chip-${tag.filterKey}-${tag.value}`}
                                label={tag.label}
                                deleteIcon={isLockedTier ? <LockClosedIcon /> : undefined}
                                onClick={isLockedTier ? undefined : () => handleRemoveFilterTag(tag.filterKey, tag.value)}
                                sx={isLockedTier ? { cursor: 'default', '&:active': { transform: 'none' } } : undefined}
                            />
                        );
                    })}
                    {showMoreLessToggle && (
                        <LinkChip
                            label={
                                isFilterChipsExpanded ? 'Show less' : `+${appliedFilterTags.length - MAX_VISIBLE_FILTER_CHIPS} more`
                            }
                            data-testid={isFilterChipsExpanded ? 'show-less-filters-chip' : 'hidden-filters-count-chip'}
                            onClick={() => setIsFilterChipsExpanded((prev) => !prev)}
                        />
                    )}
                    <Action data-testid="clear-all-filters-action" onClick={handleClearFilters}>
                        Clear All
                    </Action>
                </Box>
            )}
            {showInitialLoading && (
                <LoadingIndicatorWrapper data-testid="programs-loading-indicator">
                    <Spinner />
                    <Typography variant="h4">{LOADING_DESC}</Typography>
                </LoadingIndicatorWrapper>
            )}
            {isError && (!allTiersMode || programsNotExpired.length === 0) && (
                <Alert type="error" role="alert" title={ERROR_DESC} sx={{ '.MuiAlertTitle-root': { margin: '0 !important' } }} />
            )}
            {hasNoResults && (
                <Alert
                    type="info"
                    role="alert"
                    data-testid="no-filter-results-alert"
                    sx={{ '.MuiAlertTitle-root': { margin: '0 !important' } }}
                >
                    <Typography variant="body-sm">{NO_RESULTS_MESSAGE}</Typography>
                </Alert>
            )}
            {!showInitialLoading && !hasNoResults && (
                <ListWithOverlayWrapper>
                    <ProgramsListCompactUI
                        key={stickyOfferType}
                        data={filteredPrograms}
                        offerType={offerType}
                        dealTier={dealTier}
                        reorderTrigger={reorderTrigger}
                        recalculating={isRecalculating}
                        sortPreference={persistedSort}
                        onSortChange={handleSortChange}
                    />
                </ListWithOverlayWrapper>
            )}
            <ProgramsFiltersSlideOut
                isOpen={filterSlideOutOpen}
                onClose={handleCloseFilters}
                hasNoPrograms={programsNotExpired.length === 0}
                filterOptions={allFilterOptions}
                filters={filters}
                onFiltersChange={setFilters}
                onApplyFilters={handleApplyFilters}
                offerType={offerType}
            />
        </Box>
    );
};
